import JxForm from './JxForm';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import jxWindowManager from '../JxWindowManager/JxWindowManager';
import jxWindowCache from '../JxWindowManager/JxWindowCache';
import { WindowDisplayStates } from '../JxWindowManager/Types/WindowDisplayStates';

/**
 * JxMinimizableFormBase — adds minimize support on top of JxForm, without pushing that
 * concept into JxForm itself (most Jx dialogs never minimize, see Development.Patterns
 * rk=15c8d2b2ff8348269c79eb688bd9c378).
 *
 * JxTitleBar stays a pure painter: it only ever receives isMinimizable (bool) + windowId,
 * and on click fires ComponentNotifier.notify('minimize', windowId) — it does not know what
 * minimize means and no callback is injected into it. This base subscribes to that event,
 * filters on its own windowId (subscribe() only filters on name, target filtering is the
 * subscriber's job — see ComponentNotifier doc), and calls jxWindowManager.minimizeWindow().
 *
 * Also registers itself with jxWindowManager's minimizable registry (independent of
 * jxWindowInstanceCache — a minimizable widget isn't necessarily opened via openWindow(),
 * e.g. CommandBarWidget is mounted directly by YourLibraryTabPainter). This is what lets
 * TaskbarWidget list it once minimized.
 *
 * extend this instead of JxForm when a widget should support minimize (e.g. CommandBarWidget),
 * and pass isMinimizable={true} windowId={this.getId()} to JxTitleBar in render(). Also combine
 * `this.isMinimized` into the render's open-guard, next to props.open — see CommandBarWidget.
 */
export default class JxMinimizableFormBase extends JxForm {
    _unsubscribeMinimize = null;
    _unsubscribeWindowsChanged = null;

    componentDidMount() {
        super.componentDidMount();

        this._unsubscribeMinimize = ComponentNotifier.subscribe('minimize', this._handleMinimizeEvent);
        this._unsubscribeWindowsChanged = ComponentNotifier.subscribe('JxWindowManager', this._handleWindowsChanged);

        jxWindowManager.registerMinimizable(this.getId(), { title: this.getTaskbarTitle() });
    }

    componentWillUnmount() {
        if (this._unsubscribeMinimize) this._unsubscribeMinimize = this._unsubscribeMinimize();
        if (this._unsubscribeWindowsChanged) this._unsubscribeWindowsChanged = this._unsubscribeWindowsChanged();

        jxWindowManager.unregisterMinimizable(this.getId());

        super.componentWillUnmount();
    }

    /**
     * Filters the shared 'minimize' notify stream down to events targeting this window.
     * @param {{name: string, target: string, data: any}} e
     */
    _handleMinimizeEvent = (e) => {
        if (e.target !== this.getId()) return;
        this.handleMinimize();
    };

    /**
     * jxWindowManager fires this on any minimize/restore/register — this instance doesn't
     * live in jxWindowInstanceCache so JxWindowManager's own _painter never re-renders it.
     * Cheap forceUpdate, this.isMinimized recomputes from jxWindowCache on every render anyway.
     * @param {{name: string, target: string, data: any}} e
     */
    _handleWindowsChanged = (e) => {
        if (e.target !== 'windowsChanged') return;
        this.forceUpdate();
    };

    /**
     * Prototype method (not arrow field) so subclasses can override + call super.handleMinimize()
     * (see Development.Patterns rk=7ac11d29 — arrow class fields have no [[HomeObject]]).
     */
    handleMinimize() {
        jxWindowManager.minimizeWindow(this.getId());
    }

    /**
     * Label shown in the Taskbar. Override for a friendlier name than the raw class name
     * (e.g. CommandBarWidget -> 'Command Bar').
     * @returns {string}
     */
    getTaskbarTitle() {
        return this.name;
    }

    /**
     * True while this window's displayState is Minimized. Combine with props.open in render():
     * `if (!this.props.open || this.isMinimized) return null;`
     * @returns {boolean}
     */
    get isMinimized() {
        return jxWindowCache.getByWindowId(this.getId())?.displayState === WindowDisplayStates.Minimized;
    }
}
